"use client";

import React, { useState } from "react";
import { Sparkles, ArrowRight, CheckCircle2, XCircle, MousePointer, Copy } from "lucide-react";

interface ActionStep {
  id: string;
  label: string;
  action: string;
  dumbSuggestion: string;
  smartSuggestion: string;
  smartReason: string;
}

const STEPS: ActionStep[] = [
  {
    id: "step1",
    label: "Schritt 1: Komponente auswählen",
    action: "Du wählst eine Button-Komponente aus der Bibliothek.",
    dumbSuggestion: "5 weitere Buttons aus anderen Bibliotheken (Mantine, Chakra, Bootstrap...)",
    smartSuggestion: "Passendes Icon-Set auswählen oder Farbkontraste prüfen",
    smartReason: "Du hast dich bereits für diesen Button entschieden. Du brauchst keine Alternativen, sondern passende Ergänzungen.",
  },
  {
    id: "step2",
    label: "Schritt 2: Code kopieren",
    action: "Du kopierst den Beispiel-Code in deinen Editor.",
    dumbSuggestion: "„Das könnte dich auch interessieren: Was ist ein Button?“",
    smartSuggestion: "Nächster Schritt: Barrierefreiheit & Tastatur-Fokus testen",
    smartReason: "Weil du den Code bereits kopiert hast, bist du mitten im Bauen. Der logische nächste Schritt ist das Testen im Projekt.",
  },
  {
    id: "step3",
    label: "Schritt 3: Ladezeit optimieren",
    action: "Du liest über schnelle Ladezeiten und Bildgrößen.",
    dumbSuggestion: "Ein 50-seitiges Grundlagenbuch über Web-Geschichte.",
    smartSuggestion: "Checkliste: Automatische Bildoptimierung mit WebP & AVIF aktivieren",
    smartReason: "Keine Theorie, sondern die direkte praktische Maßnahme, die dir sofort 70% Ladezeit spart.",
  },
];

export default function SmartSuggestionsPlayground() {
  const [activeStepId, setActiveStepId] = useState<string>("step1");
  const currentStep = STEPS.find((s) => s.id === activeStepId) || STEPS[0];

  return (
    <div className="my-8 rounded-2xl border border-neutral-200/90 bg-gradient-to-b from-white via-neutral-50/50 to-neutral-100/30 p-5 shadow-xs sm:p-6 dark:border-neutral-800 dark:from-neutral-900/90 dark:via-neutral-900/50 dark:to-neutral-950">
      {/* Header */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-purple-500/10 text-purple-600 dark:bg-purple-400/10 dark:text-purple-400">
            <Sparkles className="h-4 w-4" />
          </span>
          <h3 className="font-heading text-base font-bold text-neutral-900 dark:text-white sm:text-lg">
            Interaktiver Vergleich: Dumm vs. Wirklich Mitdenkend
          </h3>
        </div>
        <span className="inline-flex items-center gap-1 rounded-full bg-purple-500/10 px-2.5 py-0.5 text-[11px] font-medium text-purple-700 dark:text-purple-300">
          Workflow statt Zufall
        </span>
      </div>

      {/* Step selector tabs */}
      <div className="mb-4 flex flex-wrap gap-2">
        {STEPS.map((step) => (
          <button
            key={step.id}
            onClick={() => setActiveStepId(step.id)}
            className={`rounded-xl px-3 py-1.5 text-xs font-medium transition-all ${
              activeStepId === step.id
                ? "bg-neutral-900 text-white shadow-xs dark:bg-white dark:text-neutral-900"
                : "border border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300 hover:bg-neutral-50 dark:border-neutral-800 dark:bg-neutral-850 dark:text-neutral-300 dark:hover:bg-neutral-800"
            }`}
          >
            {step.label}
          </button>
        ))}
      </div>

      {/* Current action card */}
      <div className="mb-4 rounded-xl border border-neutral-200 bg-white p-3.5 dark:border-neutral-800 dark:bg-neutral-850">
        <div className="flex items-center gap-2 text-xs font-semibold text-neutral-500 dark:text-neutral-400 mb-1">
          <MousePointer className="h-3.5 w-3.5 text-blue-500" />
          <span>Was du gerade tust:</span>
        </div>
        <p className="text-sm font-medium text-neutral-900 dark:text-white">
          {currentStep.action}
        </p>
      </div>

      {/* Comparison Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {/* Dumb suggestion */}
        <div className="rounded-xl border border-red-200/80 bg-red-50/40 p-4 dark:border-red-900/40 dark:bg-red-950/20">
          <div className="flex items-center gap-1.5 text-xs font-bold text-red-700 dark:text-red-400 mb-1.5">
            <XCircle className="h-4 w-4 shrink-0 text-red-500" />
            <span>Klassisch („Kunden kauften auch“):</span>
          </div>
          <p className="text-sm font-medium text-neutral-800 dark:text-neutral-200 mb-2">
            {currentStep.dumbSuggestion}
          </p>
          <p className="text-xs text-neutral-500 dark:text-neutral-400">
            ❌ <strong>Problem:</strong> Zeigt dir nur mehr vom Gleichen, das du längst nicht mehr brauchst.
          </p>
        </div>

        {/* Smart suggestion */}
        <div className="rounded-xl border border-emerald-200/80 bg-emerald-50/40 p-4 dark:border-emerald-900/40 dark:bg-emerald-950/20">
          <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-700 dark:text-emerald-400 mb-1.5">
            <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />
            <span>Smarter Vorschlag (Der nächste logische Schritt):</span>
          </div>
          <p className="text-sm font-semibold text-neutral-900 dark:text-white mb-2">
            {currentStep.smartSuggestion}
          </p>
          <p className="text-xs text-neutral-600 dark:text-neutral-300">
            ✅ <strong>Warum das hilft:</strong> {currentStep.smartReason}
          </p>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-3.5 flex items-center justify-between text-[11px] text-neutral-400 dark:text-neutral-500 pt-2 border-t border-neutral-100 dark:border-neutral-800/60">
        <span>Klicke oben auf die Schritte, um zu sehen, wie sich die Empfehlung verändert.</span>
        <span>100% datenschutzkonform im Browser</span>
      </div>
    </div>
  );
}
